/**
 * Tracker campaigns for the override picker, normalised to CampaignOption.
 *
 * Reuses useAllCampaigns (Voluum + Binom, + Tres Commas where enabled) rather
 * than adding an endpoint. That hook pulls the whole list into memory, which is
 * why the picker is search-gated instead of rendering everything. A row whose
 * tracker cannot carry tag-rule overrides (campaignRowScope → null) is never offered.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo } from 'react';

import { useAllCampaigns } from '@/hooks';
import { campaignRowScope } from '@/features/tag-rule-scope/ui/campaign-scope-options';

import type { CampaignOption } from './campaign-threshold-parts';

interface UseCampaignOptionsResult {
  options: CampaignOption[];
  isLoading: boolean;
}

function countryNameOf(country: unknown): string | undefined {
  if (typeof country === 'string') return country;
  if (country && typeof country === 'object') {
    const name = (country as { name?: unknown }).name;
    if (typeof name === 'string') return name;
  }
  return undefined;
}

export function useCampaignOptions(): UseCampaignOptionsResult {
  const { data, isLoading } = useAllCampaigns();
  const options = useMemo<CampaignOption[]>(() => {
    return (data ?? [])
      .filter((c) => typeof c.id === 'string' && c.id.trim().length > 0)
      .flatMap((c): CampaignOption[] => {
        const scope = campaignRowScope(c);
        if (!scope) return [];
        const name = c.name?.trim() || String(c.id);
        const countryName = countryNameOf((c as { country?: unknown }).country);
        return [{ id: String(c.id), name, provider: scope.provider, countryName }];
      });
  }, [data]);
  return { options, isLoading };
}
